/**
 * Property identity and publication helpers.
 *
 * New multi-property code must resolve a property from an explicit slug or ID.
 * Legacy public routes may call resolveLegacyDefaultPropertyId() so the live
 * Kualoa site keeps working. That helper never uses LIMIT 1.
 */

export const KUALOA_PROPERTY_SLUG = "the-kualoa-beach-house";
/** The slug the Kualoa house had before it moved under /properties. Still resolves. */
export const KUALOA_LEGACY_SLUG = "kualoa-beach-house";
/** The second published home. */
export const CALDWELL_PROPERTY_SLUG = "caldwell-beach-house";
/** The home that just joined the site. Its cards and pages carry a "New Listing!" badge until this date. */
export const NEW_LISTING_SLUG = CALDWELL_PROPERTY_SLUG;
export const NEW_LISTING_UNTIL = "2026-12-31";
export function isNewListing(slug: string | null | undefined, today = new Date().toISOString().slice(0, 10)): boolean {
  return Boolean(slug) && slug === NEW_LISTING_SLUG && today <= NEW_LISTING_UNTIL;
}
/** The Kualoa listing page. Every internal link to "the house" goes here. */
export const KUALOA_STAY_PATH = `/properties/${KUALOA_PROPERTY_SLUG}`;
export const PROPERTY_2_DRAFT_SLUG = "second-oahu-home";

/** Canonical slug for any spelling of the Kualoa slug, unchanged for others. */
export function canonicalPropertySlug(slug: string): string {
  return slug === KUALOA_LEGACY_SLUG ? KUALOA_PROPERTY_SLUG : slug;
}

/** Verified production UUID for Property 1. Prefer DEFAULT_PROPERTY_ID env. */
export const KUALOA_PROPERTY_UUID = "768ddc2d-04b5-47d2-88bc-9ed6ddfa6554";

export const PUBLICATION_STATUSES = ["draft", "published", "archived"] as const;
export type PublicationStatus = (typeof PUBLICATION_STATUSES)[number];

export const FEATURE_FLAGS = {
  portfolioHomepage: "FEATURE_PORTFOLIO_HOMEPAGE",
  propertyRoutes: "FEATURE_PROPERTY_ROUTES",
  adminPropertySwitcher: "FEATURE_ADMIN_PROPERTY_SWITCHER",
  property2Public: "FEATURE_PROPERTY_2_PUBLIC",
  sharedReviews: "FEATURE_SHARED_REVIEWS",
} as const;

export type FeatureFlagName = keyof typeof FEATURE_FLAGS;

function envFlag(name: string, defaultOn = false): boolean {
  const raw = process.env[name];
  if (raw == null || raw === "") return defaultOn;
  return raw === "1" || raw.toLowerCase() === "true" || raw.toLowerCase() === "yes";
}

export function isFeatureEnabled(flag: FeatureFlagName): boolean {
  switch (flag) {
    case "portfolioHomepage":
      // On by default: the homepage only switches to the portfolio view once
      // more than one home is published, so a single-home site is unchanged.
      // Set FEATURE_PORTFOLIO_HOMEPAGE=false to keep the single-home page.
      return envFlag(FEATURE_FLAGS.portfolioHomepage, true);
    case "propertyRoutes":
      // On by default: every home, the Kualoa house included, lives under
      // /properties/<slug>, and /stay redirects there.
      return envFlag(FEATURE_FLAGS.propertyRoutes, true);
    case "adminPropertySwitcher":
      return envFlag(FEATURE_FLAGS.adminPropertySwitcher, true);
    case "property2Public":
      // The second home is published from the admin (status + show on website).
      // The env var is only an emergency off switch, so it defaults on.
      return envFlag(FEATURE_FLAGS.property2Public, true);
    case "sharedReviews":
      // Off by default: each review carries its own "shown on" list of homes,
      // managed in the admin, so sharing is a per-review choice rather than a
      // site-wide switch. Set FEATURE_SHARED_REVIEWS=true to fall back to
      // showing the first home's reviews everywhere.
      return envFlag(FEATURE_FLAGS.sharedReviews, false);
    default:
      return false;
  }
}

export function getPublicFeatureFlags() {
  return {
    portfolioHomepage: isFeatureEnabled("portfolioHomepage"),
    propertyRoutes: isFeatureEnabled("propertyRoutes"),
    property2Public: isFeatureEnabled("property2Public"),
    sharedReviews: isFeatureEnabled("sharedReviews"),
  };
}

export function shouldShowPortfolioNav(publishedHomeCount: number): boolean {
  return publishedHomeCount > 1;
}

/**
 * Temporary compatibility only. Never call this from new property-scoped APIs,
 * background jobs, admin mutations, or Property 2 workflows.
 */
export function readConfiguredDefaultPropertyId(): string | null {
  const fromEnv = process.env.DEFAULT_PROPERTY_ID?.trim();
  if (fromEnv && isUuid(fromEnv)) return fromEnv;
  return null;
}

export function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function isPubliclyVisible(property: {
  publication_status?: string | null;
  show_on_website?: boolean | null;
}): boolean {
  return property.publication_status === "published" && property.show_on_website === true;
}

export function isBookable(property: {
  publication_status?: string | null;
  show_on_website?: boolean | null;
  booking_enabled?: boolean | null;
}): boolean {
  return isPubliclyVisible(property) && property.booking_enabled === true;
}

/** Fields that must never appear on public property DTOs or client caches. */
export const SENSITIVE_PROPERTY_FIELDS = [
  "address",
  "address_line",
  "lat",
  "lng",
  "gate_code",
  "door_code",
  "wifi_network",
  "wifi_password",
  "driving_directions",
  "parking_instructions",
  "directions_url",
  "house_manual",
  "checkout_instructions",
  "emergency_contact_name",
  "emergency_contact_phone",
  "housekeeping_contact",
  "maintenance_contact",
  "permit_number",
  "tax_id",
  "city",
  "state",
  "postal_code",
  "country",
] as const;

export type SensitivePropertyField = (typeof SENSITIVE_PROPERTY_FIELDS)[number];

export function stripSensitivePropertyFields<T extends Record<string, unknown>>(
  record: T,
): Omit<T, SensitivePropertyField> {
  const copy = { ...record };
  for (const field of SENSITIVE_PROPERTY_FIELDS) {
    delete copy[field];
  }
  return copy;
}

export function propertyCanonicalPath(slug: string): string {
  return `/properties/${slug}`;
}

export function propertyBookingPath(slug: string): string {
  return `/properties/${slug}/book`;
}

export function propertyReviewsPath(slug: string): string {
  return `/properties/${slug}/reviews`;
}

export function stripePropertyMetadata(property?: {
  id?: string;
  slug?: string | null;
  title?: string | null;
}) {
  return {
    property_id: property?.id || "",
    property_slug: property?.slug || "",
    property_name: property?.title || "",
  };
}
